import { Injectable } from '@angular/core';
import { PscHttpService } from "../psc-http.service";
import { StorageKeyEnum } from "../storage/storage-key.enum";
import { HttpContext, HttpResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { StorageService } from "../storage/storage.service";
import { BehaviorSubject, map, tap } from "rxjs";
import { SKIP_STATUS_CODES } from "../../common/interceptors/error-response-handler.interceptor";
import { UserRole } from '../../common/models/user/user-role.enum';
import { BasicUserData } from '../../common/models/user/basic-user-data';
import { WarningBarService } from '../warning-bar.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {


  baseEndpoint: string = 'auth/';
  readonly tokenHeaderKey = 'X-API-authentication-token';


  private userData: BehaviorSubject<BasicUserData | null> = new BehaviorSubject<BasicUserData | null>(null);

  constructor(
    private httpService: PscHttpService,
    private router: Router,
    private storageService: StorageService,
    private warningBarService: WarningBarService,
  ) {
  }

  getUserDataObservable() {
    return this.userData.asObservable();
  }

  getUserDataValue() {
    return this.userData.value;
  }


  private setToken(token: string, tokenType: StorageKeyEnum = StorageKeyEnum.AccessToken) {
    return this.storageService.setItem(tokenType, token);
  }

  private clearToken(tokenType: StorageKeyEnum = StorageKeyEnum.AccessToken) {
    this.storageService.removeItem(tokenType);
  }

  getToken(tokenType: StorageKeyEnum = StorageKeyEnum.AccessToken): string {
    return this.storageService.getItem(tokenType) as string;
  }

  userLoggedIn(): boolean {
    return !!this.getToken();
  }

  getUserEmail() {
    return this.userData.value?.email || '';
  }

  userHasRoles(roles: UserRole[]): boolean {
    if(!roles) {
      return false;
    }
    const storedRoles = this.userData.value?.authorities;
    if(!storedRoles || storedRoles.length === 0) {
      return false;
    }
    for (const role of storedRoles) {
      if (roles.includes(role)) {
        return true;
      }
    }
    return false;
  }

  userHasRole(role: UserRole) {
    return this.userHasRoles([role]);
  }
  tokenExpired() {
    this.logout(true);
  }

  logout(loginExpired: boolean = false) {
    this.clearToken();
    this.storageService.removeItem(StorageKeyEnum.AccessToken);
    this.storageService.removeItem(StorageKeyEnum.RefreshToken);
    this.userData.next(null);
    this.warningBarService.hideWarningBar();
    this.router.navigate(['/login'], loginExpired ? { queryParams: { loginExpired: loginExpired } } : {});
  }

  login(email: string, password: string) {
    return this.httpService.post(`${ this.baseEndpoint }login`, { email, password }, {
      observe: 'response',
      context: new HttpContext().set(SKIP_STATUS_CODES, [400, 401]),
    }).pipe(
      map(
        (response: HttpResponse<BasicUserData>) => {
          const token = response.headers.get(this.tokenHeaderKey) as string;
          const user = response.body as BasicUserData;
          this.setToken(token);
          this.saveUserData(user);
          return response.body;
        },
      ),
      tap(() => {
        this.warningBarService.checkWarnings();
      }),
    );
  }

  userCheck() {
    return this.httpService.get(`${ this.baseEndpoint }userCheck`).pipe(
      tap(
        (data: BasicUserData) => {
          this.saveUserData(data);
          this.warningBarService.checkWarnings();
        },
      ));
  }

  saveUserData(user: BasicUserData) {
    this.userData.next(user);
  }

  userHasExpiredRole() {
    return this.userHasRoles([UserRole.ROLE_BUSINESS_EXP, UserRole.ROLE_BUSINESS_PLUS_EXP]);
  }
}
